/**
 * One fixed tooltip layer for the review page, positioned on hover.
 *
 * Detail rides in tooltips throughout this page — the ops AI-coach card already
 * works this way — so the layer is shared rather than one per surface: a single
 * fixed element cannot be clipped by whatever scroll container the target sits
 * in, and only one tip is ever on screen.
 */

import { useEffect, useRef, useState } from "react";

export interface TipContent { text: string; sub?: string }

const TipCtx = {
  show: (_el: HTMLElement, _c: TipContent) => {},
  hide: () => {},
};

export function Tip({ text, sub, children, className }: {
  text: string;
  sub?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  return (
    <span
      className={`pr-has-tip ${className || ""}`}
      onBlur={() => TipCtx.hide()}
      onFocus={() => ref.current && TipCtx.show(ref.current, { text, sub })}
      onMouseEnter={() => ref.current && TipCtx.show(ref.current, { text, sub })}
      onMouseLeave={() => TipCtx.hide()}
      ref={ref}
      tabIndex={0}
    >
      {children}
    </span>
  );
}

export function TipLayer() {
  const [tip, setTip] = useState<
    { c: TipContent; top: number; left: number } | null
  >(null);

  useEffect(() => {
    TipCtx.show = (el, c) => {
      const r = el.getBoundingClientRect();
      // Above the target, clamped into the viewport so a tooltip on a column
      // scrolled to the far right still lands on screen.
      const left = Math.min(Math.max(8, r.left), window.innerWidth - 356);
      setTip({ c, top: Math.max(8, r.top - 8), left });
    };
    TipCtx.hide = () => setTip(null);
    return () => { TipCtx.show = () => {}; TipCtx.hide = () => {}; };
  }, []);

  if (!tip) return null;
  return (
    <div
      className="pr-tip-layer"
      style={{ top: tip.top, left: tip.left, transform: "translateY(-100%)" }}
    >
      {tip.c.text}
      {tip.c.sub && <span className="pr-tip-sub">{tip.c.sub}</span>}
    </div>
  );
}
